import type { ReactNode } from 'react';

const labelMap: Record<string, string> = {
  active: 'Active',
  trial: 'Trial',
  trialing: 'Trial',
  suspended: 'Suspended',
  archived: 'Archived',
  healthy: 'Healthy',
  degraded: 'Degraded',
  unavailable: 'Unavailable',
  unknown: 'Unknown',
  unverified: 'Unverified',
  success: 'Success',
  failed: 'Failed',
  failure: 'Failed',
  warning: 'Warning',
  operational: 'Operational',
  in_progress: 'In progress',
  needs_attention: 'Needs attention',
  waiting: 'Waiting',
  resolved: 'Resolved',
  escalated: 'Escalated',
  committed: 'Imported',
  connected: 'Connected',
  past_due: 'Past due',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  not_configured: 'Not set up',
  published: 'Sent',
  processing: 'Working',
  pending: 'Waiting',
  pending_mapping: 'Needs mapping',
  pending_commit: 'Ready to import',
  reauth_required: 'Needs sign-in',
  disconnected: 'Disconnected',
  inactive: 'Off',
  disabled: 'Off',
  open: 'Open',
};

const classMap: Record<string, string> = {
  active: 'badge-active',
  healthy: 'badge-active',
  success: 'badge-active',
  operational: 'badge-active',
  resolved: 'badge-active',
  committed: 'badge-active',
  connected: 'badge-active',
  trial: 'badge-warning',
  trialing: 'badge-warning',
  degraded: 'badge-warning',
  warning: 'badge-warning',
  waiting: 'badge-warning',
  pending: 'badge-warning',
  pending_mapping: 'badge-warning',
  pending_commit: 'badge-warning',
  in_progress: 'badge-info',
  suspended: 'badge-danger',
  unavailable: 'badge-danger',
  failed: 'badge-danger',
  failure: 'badge-danger',
  past_due: 'badge-danger',
  needs_attention: 'badge-danger',
  escalated: 'badge-danger',
  error: 'badge-danger',
  archived: 'badge-archived',
  cancelled: 'badge-archived',
  canceled: 'badge-archived',
  unknown: 'badge-unknown',
  unverified: 'badge-unknown',
  disconnected: 'badge-unknown',
  not_configured: 'badge-unknown',
  inactive: 'badge-unknown',
  disabled: 'badge-unknown',
  reauth_required: 'badge-warning',
  published: 'badge-active',
  processing: 'badge-info',
  open: 'badge-warning',
};

const dotMap: Record<string, string> = {
  active: 'status-dot-green',
  healthy: 'status-dot-green',
  success: 'status-dot-green',
  operational: 'status-dot-green',
  resolved: 'status-dot-green',
  committed: 'status-dot-green',
  connected: 'status-dot-green',
  trial: 'status-dot-amber',
  trialing: 'status-dot-amber',
  degraded: 'status-dot-amber',
  warning: 'status-dot-amber',
  waiting: 'status-dot-amber',
  pending: 'status-dot-amber',
  pending_mapping: 'status-dot-amber',
  pending_commit: 'status-dot-amber',
  suspended: 'status-dot-red',
  unavailable: 'status-dot-red',
  failed: 'status-dot-red',
  failure: 'status-dot-red',
  past_due: 'status-dot-red',
  needs_attention: 'status-dot-red',
  escalated: 'status-dot-red',
  error: 'status-dot-red',
  unknown: 'status-dot-gray',
  unverified: 'status-dot-gray',
  in_progress: 'status-dot-green',
  archived: 'status-dot-gray',
  cancelled: 'status-dot-gray',
  canceled: 'status-dot-gray',
  disconnected: 'status-dot-gray',
};

export function StatusBadge({ status, dot }: { status: string; dot?: boolean }) {
  const key = status.toLowerCase().replaceAll(' ', '_');
  const cls = classMap[key] || 'badge-unknown';
  const label = labelMap[key] || status.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const dotCls = dotMap[key] || 'status-dot-gray';

  return (
    <span className={`badge ${cls}`}>
      {dot && <span className={`status-dot ${dotCls}`} />}
      {label}
    </span>
  );
}

export type AdminBadgeVariant = 'plan' | 'active' | 'danger' | 'muted' | 'warn' | 'info' | 'neutral';

export function AdminBadge({
  variant = 'neutral',
  children,
}: {
  variant?: AdminBadgeVariant;
  children: ReactNode;
}) {
  const map: Record<AdminBadgeVariant, string> = {
    plan: 'badge-info',
    active: 'badge-active',
    danger: 'badge-danger',
    muted: 'badge-archived',
    warn: 'badge-warning',
    info: 'badge-info',
    neutral: 'badge-unknown',
  };
  return <span className={`badge ${map[variant]} admin-badge admin-badge--${variant}`}>{children}</span>;
}

export function OrgStatusBadge({ status }: { status: 'active' | 'suspended' | 'deleted' | string }) {
  const variant: AdminBadgeVariant =
    status === 'suspended' ? 'danger' : status === 'deleted' ? 'muted' : status === 'active' ? 'active' : 'neutral';
  return <AdminBadge variant={variant}>{status.replaceAll('_', ' ')}</AdminBadge>;
}

export function OrgPlanBadge({ plan }: { plan: string }) {
  return <AdminBadge variant="plan">{plan}</AdminBadge>;
}

export function FlagModeBadge({ mode }: { mode: string }) {
  const labels: Record<string, string> = { OFF: 'Off', ON: 'On', PER_ORG: 'Per organization' };
  return <AdminBadge variant="info">{labels[mode] ?? mode.replace(/_/g, ' ')}</AdminBadge>;
}

export function AnnouncementStatusBadge({ status }: { status: string }) {
  return <StatusBadge status={status} />;
}
